import api from "features/api";
import { useAuth } from "features/store";

export interface FeatureFlag {
  id: number;
  /** Immutable once created: the backend refuses a rename. */
  name: string;
  description: string;
  enabled: boolean;
  created_on: string;
  updated_on: string | null;
  /** True once the flag is more than six months old. */
  is_hidden: boolean;
}

export interface ListFeatureFlagsParams {
  includeHidden: boolean;
}

export interface CreateFeatureFlagParams {
  name: string;
  description: string;
}

export interface ToggleFeatureFlagParams {
  id: number;
  enabled: boolean;
}

/** Every flag's name mapped to whether it is on; readable by any user. */
export type FeatureFlagStates = Record<string, boolean>;

/** The backend's rule for a name, restated so the form can say so first. */
export const FEATURE_FLAG_NAME_PATTERN = /^[a-z0-9_-]{1,100}$/;

const LIST_TAG = { type: "FeatureFlag" as const, id: "LIST" };
const STATES_TAG = { type: "FeatureFlag" as const, id: "STATES" };

export const apiWithFeatureFlags = api.injectEndpoints({
  endpoints: (builder) => ({
    listFeatureFlags: builder.query<FeatureFlag[], ListFeatureFlagsParams>({
      query: ({ includeHidden }) => ({
        url: `/feature-flags/`,
        params: includeHidden ? { include_hidden: "true" } : undefined,
      }),
      providesTags: (result) => [
        LIST_TAG,
        ...(result ?? []).map(({ id }) => ({
          type: "FeatureFlag" as const,
          id,
        })),
      ],
    }),

    createFeatureFlag: builder.mutation<FeatureFlag, CreateFeatureFlagParams>({
      query: (body) => ({ url: `/feature-flags/`, method: "POST", body }),
      invalidatesTags: [LIST_TAG, STATES_TAG],
    }),

    toggleFeatureFlag: builder.mutation<FeatureFlag, ToggleFeatureFlagParams>({
      query: ({ id, enabled }) => ({
        url: `/feature-flags/${id}/`,
        method: "PATCH",
        body: { enabled },
      }),
      // The switch moves at once in every cached list (with and without the
      // hidden flags) and moves back if the server refuses.
      async onQueryStarted({ id, enabled }, { dispatch, queryFulfilled }) {
        const patches = [false, true].map((includeHidden) =>
          dispatch(
            apiWithFeatureFlags.util.updateQueryData(
              "listFeatureFlags",
              { includeHidden },
              (flags) => {
                const flag = flags.find((item) => item.id === id);
                if (flag) {
                  flag.enabled = enabled;
                }
              },
            ),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "FeatureFlag", id },
        STATES_TAG,
      ],
    }),

    deleteFeatureFlag: builder.mutation<void, number>({
      query: (id) => ({ url: `/feature-flags/${id}/`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "FeatureFlag", id },
        LIST_TAG,
        STATES_TAG,
      ],
    }),

    getFeatureFlagStates: builder.query<FeatureFlagStates, void>({
      query: () => `/feature-flags/states/`,
      providesTags: [STATES_TAG],
    }),
  }),
});

export const {
  useListFeatureFlagsQuery,
  useCreateFeatureFlagMutation,
  useToggleFeatureFlagMutation,
  useDeleteFeatureFlagMutation,
  useGetFeatureFlagStatesQuery,
} = apiWithFeatureFlags;

/**
 * Whether a feature flag is on, for gating product code. An unknown flag, or
 * any flag before the states have loaded, is off: a gated feature must never
 * flash on and then vanish.
 */
export const useFeatureFlag = (name: string): boolean => {
  const { isAuthenticated } = useAuth();
  const { data } = useGetFeatureFlagStatesQuery(undefined, {
    skip: !isAuthenticated,
  });
  // `hasOwn`, not a bare lookup: a flag named after an Object.prototype
  // member would otherwise read a function off the prototype.
  return data !== undefined && Object.hasOwn(data, name) && data[name] === true;
};
